import axios from 'axios';
import type { ApiError } from '../types';

export type ApiErrorCategory =
  | 'offline'
  | 'timeout'
  | 'network'
  | 'unauthorized'
  | 'forbidden'
  | 'notFound'
  | 'validation'
  | 'conflict'
  | 'serverError'
  | 'unexpected';

export interface ClassifiedApiError {
  category: ApiErrorCategory;
  status?: number;
  serverMessage?: string;
  raw: unknown;
}

const CATEGORY_TO_I18N: Record<ApiErrorCategory, string> = {
  offline: 'errors.offline',
  timeout: 'errors.timeout',
  network: 'errors.network',
  unauthorized: 'auth.invalidCredentials',
  forbidden: 'errors.forbidden',
  notFound: 'errors.notFound',
  validation: 'errors.validation',
  conflict: 'errors.conflict',
  serverError: 'errors.serverError',
  unexpected: 'errors.unexpected',
};

export function classifyApiError(error: unknown): ClassifiedApiError {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { category: 'offline', raw: error };
  }
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const serverMessage = (error.response?.data as ApiError | undefined)?.error?.message;

    if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
      return { category: 'timeout', raw: error };
    }
    if (error.code === 'ERR_NETWORK' || !error.response) {
      return { category: 'network', raw: error };
    }
    if (status === 401) return { category: 'unauthorized', status, serverMessage, raw: error };
    if (status === 403) return { category: 'forbidden', status, serverMessage, raw: error };
    if (status === 404) return { category: 'notFound', status, serverMessage, raw: error };
    if (status === 409) return { category: 'conflict', status, serverMessage, raw: error };
    if (status === 422 || status === 400) return { category: 'validation', status, serverMessage, raw: error };
    if (status && status >= 500) return { category: 'serverError', status, serverMessage, raw: error };
  }
  return { category: 'unexpected', raw: error };
}

export function i18nKeyForCategory(category: ApiErrorCategory): string {
  return CATEGORY_TO_I18N[category];
}

export type Translator = (key: string) => string;

export function toErrorMessage(error: unknown, t?: Translator): string {
  const classified = classifyApiError(error);
  if (classified.serverMessage) return classified.serverMessage;
  if (t) return t(i18nKeyForCategory(classified.category));
  if (error instanceof Error) return error.message;
  return 'An unexpected error occurred.';
}
